/**
 * Patrón de 2 pestañas del ERP (decisión de Germán, 18-oct-2026). Aplica a TODAS las pantallas del ERP.
 *
 *   · «Consultar»: listado, búsqueda y filtros. La ven todos los que entran a la pantalla. Se abre por omisión.
 *   · «Agregar / editar»: el formulario. Sólo existe si el usuario puede dar de alta o editar; si no, ni se pinta.
 *   · «+ Agregar» (arriba, sólo con permiso de alta): abre el formulario en blanco.
 *   · El lápiz del listado abre «Agregar / editar» con el registro cargado. Guardar o Cancelar regresan a «Consultar».
 *
 * Esto es sólo la vista: el candado REAL está en el servidor (requirePermission en cada POST/PUT/DELETE).
 *
 * Uso:
 *   import { permisosPantalla, pestanasHtml, Pestanas } from '../../utils/pestanasErp.js';
 *
 *   render() {
 *     const pp = permisosPantalla('productos');           // { puedeCrear, puedeEditar, conFormulario }
 *     return pestanasHtml({ id: 'prod', titulo: 'Productos', ...pp, consultar: '<listado>', formulario: '<form>' });
 *   }
 *   afterRender() {
 *     this.tabs = new Pestanas('prod', { alAgregar: () => this.limpiar() });
 *   }
 *   editar(id)  → … carga el registro … ; this.tabs.abrirFormulario({ editando: true, etiqueta: p.clave });
 *   guardar()   → … si salió bien … ; this.limpiar(); this.tabs.volver();
 *   Cancelar    → this.limpiar(); this.tabs.volver();
 */

import permisos from '../services/permissions.service.js';
import '../styles/erp_pestanas.css';

const escH = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Lo que el usuario puede hacer en la pantalla (admin puede todo). */
export function permisosPantalla(modulo, { crear = 'crear', editar = 'editar' } = {}) {
  const puedeCrear = !!(crear && permisos.can(modulo, crear));
  const puedeEditar = !!(editar && permisos.can(modulo, editar));
  return { puedeCrear, puedeEditar, conFormulario: puedeCrear || puedeEditar };
}

/**
 * HTML de la pantalla con sus dos pestañas.
 * @param {object} o
 * @param {string} o.id            prefijo único de la pantalla (ids de los botones y paneles)
 * @param {string} o.titulo        título de la página
 * @param {boolean} o.puedeCrear   muestra «+ Agregar»
 * @param {boolean} o.conFormulario muestra la pestaña «Agregar / editar» (alta O edición)
 * @param {string} o.consultar     HTML del listado (búsqueda, filtros, tabla)
 * @param {string} [o.formulario]  HTML del formulario (se omite si no hay permiso)
 * @param {string} [o.arriba]      HTML común arriba de las pestañas (avisos que aplican a las dos)
 * @param {string} [o.textoAgregar] texto del botón (por omisión «+ Agregar»)
 * @param {string} [o.avisoSoloEdicion] texto cuando el usuario puede editar pero no dar de alta y no hay nada cargado
 */
export function pestanasHtml(o) {
  const conForm = !!(o.conFormulario && o.formulario);
  const id = o.id;
  return `
    <div class="page-header">
      <h1>${escH(o.titulo)}</h1>
      ${conForm && o.puedeCrear ? `<button type="button" class="btn btn-primary" id="${id}_btnAgregar">${escH(o.textoAgregar || '+ Agregar')}</button>` : ''}
    </div>
    ${o.arriba || ''}
    <div class="erp-pestanas" role="tablist" id="${id}_pestanas">
      <button type="button" class="erp-pestana activa" role="tab" aria-selected="true" data-pestana="consultar" id="${id}_tabConsultar">Consultar</button>
      ${conForm ? `<button type="button" class="erp-pestana" role="tab" aria-selected="false" data-pestana="formulario" id="${id}_tabFormulario">Agregar / editar<span class="erp-pestana-etiqueta" id="${id}_tabEtiqueta"></span></button>` : ''}
    </div>
    <div class="erp-panel activo" role="tabpanel" data-panel="consultar" id="${id}_panelConsultar">
      ${o.consultar || ''}
    </div>
    ${conForm ? `
    <div class="erp-panel" role="tabpanel" data-panel="formulario" id="${id}_panelFormulario" hidden>
      ${o.puedeCrear ? '' : `<div class="erp-aviso erp-aviso-info erp-solo-edicion" id="${id}_soloEdicion">${escH(o.avisoSoloEdicion || 'Puedes editar, pero no dar de alta. Elige un registro con el lápiz ✏️ en «Consultar».')}</div>`}
      <div class="erp-panel-cuerpo" id="${id}_cuerpoFormulario" ${o.puedeCrear ? '' : 'hidden'}>
        ${o.formulario}
      </div>
    </div>` : ''}
  `;
}

/** Controlador de las pestañas de una pantalla (se crea en afterRender). */
export class Pestanas {
  constructor(id, { alAgregar = null, alCambiar = null } = {}) {
    this.id = id;
    this.alAgregar = alAgregar;
    this.alCambiar = alCambiar;
    this.editando = false;
    this.actual = 'consultar';
    const $ = (s) => document.getElementById(`${id}_${s}`);
    this.el = {
      tabC: $('tabConsultar'), tabF: $('tabFormulario'), etiqueta: $('tabEtiqueta'),
      panelC: $('panelConsultar'), panelF: $('panelFormulario'),
      soloEdicion: $('soloEdicion'), cuerpo: $('cuerpoFormulario'), agregar: $('btnAgregar'),
      barra: $('pestanas')
    };
    this.el.tabC?.addEventListener('click', () => this.mostrar('consultar'));
    this.el.tabF?.addEventListener('click', () => this.mostrar('formulario'));
    this.el.agregar?.addEventListener('click', () => {
      if (this.alAgregar) this.alAgregar();
      this.abrirFormulario({ editando: false });
    });
  }

  /** ¿La pantalla tiene pestaña de formulario (el usuario puede dar de alta o editar)? */
  get conFormulario() { return !!this.el.panelF; }

  mostrar(nombre) {
    if (nombre === 'formulario' && !this.el.panelF) nombre = 'consultar';
    this.actual = nombre;
    const f = nombre === 'formulario';
    this.el.tabC?.classList.toggle('activa', !f);
    this.el.tabC?.setAttribute('aria-selected', String(!f));
    this.el.tabF?.classList.toggle('activa', f);
    this.el.tabF?.setAttribute('aria-selected', String(f));
    if (this.el.panelC) { this.el.panelC.hidden = f; this.el.panelC.classList.toggle('activo', !f); }
    if (this.el.panelF) { this.el.panelF.hidden = !f; this.el.panelF.classList.toggle('activo', f); }
    this._pintarSoloEdicion();
    const barra = this.el.barra;
    if (barra && barra.getBoundingClientRect().top < 0) barra.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (this.alCambiar) this.alCambiar(nombre);
  }

  /** Abre «Agregar / editar». Con editando=true muestra la etiqueta del registro en la pestaña. */
  abrirFormulario({ editando = false, etiqueta = '' } = {}) {
    this.marcarEdicion(editando, etiqueta);
    this.mostrar('formulario');
  }

  /** Marca si el formulario tiene un registro cargado (sin cambiar de pestaña). */
  marcarEdicion(editando, etiqueta = '') {
    this.editando = !!editando;
    if (this.el.etiqueta) this.el.etiqueta.textContent = editando && etiqueta ? ` · ${etiqueta}` : '';
    this.el.tabF?.classList.toggle('editando', !!editando);
    this._pintarSoloEdicion();
  }

  /** Regresa a «Consultar» (después de Guardar o Cancelar). */
  volver() {
    this.marcarEdicion(false);
    this.mostrar('consultar');
  }

  _pintarSoloEdicion() {
    // Sin permiso de alta: el formulario sólo se muestra con un registro cargado desde el lápiz.
    if (!this.el.soloEdicion) return;
    this.el.soloEdicion.hidden = this.editando;
    if (this.el.cuerpo) this.el.cuerpo.hidden = !this.editando;
  }
}
